import { DiscountValueType } from 'app/shared/model';
import { EditableDiscountBand } from 'app/pages/DiscountEditor/modules/DiscountActions';
import { monetaryAmountKeyPart } from 'app/pages/DiscountEditor/components/EditDiscountActionDialog/discountBandFieldValidation';

export const DUPLICATE_DISCRETE_UNIT_PRICE_TIER_MESSAGE =
    'Another tier already uses this minimum quantity, unit price, and currency.';

function moneyTierKey(
    band: Readonly<EditableDiscountBand>,
    currencyCode: string | undefined
): string | undefined {
    const cur = currencyCode?.trim();
    if (!cur || band.from === '' || band.value === '') return undefined;

    const minUnits = parseInt(band.from, 10);
    if (!Number.isFinite(minUnits)) return undefined;

    const amountKey = monetaryAmountKeyPart(band.value);
    if (amountKey === undefined) return undefined;

    return `${minUnits}\0${amountKey}\0${cur}`;
}

export function mergeDiscreteDuplicateTierValidation(
    bands: Readonly<EditableDiscountBand>[],
    valueType: DiscountValueType,
    currencyCode: string | undefined
): EditableDiscountBand[] {
    if (
        valueType !== DiscountValueType.AmountPerUnit &&
        valueType !== DiscountValueType.OverrideUnitPrice
    ) {
        return [...bands];
    }

    const dupMsg = DUPLICATE_DISCRETE_UNIT_PRICE_TIER_MESSAGE;
    const keys = bands.map(b => moneyTierKey(b, currencyCode));
    const counts = new Map<string, number>();
    for (const k of keys) {
        if (k === undefined) continue;
        counts.set(k, (counts.get(k) || 0) + 1);
    }

    return bands.map((band, i) => {
        const k = keys[i];
        const isDupGroup = k !== undefined && (counts.get(k) ?? 0) > 1;

        const hasBlockingError =
            !!(band.fromMessage && band.fromMessage !== dupMsg) ||
            !!(band.valueMessage && band.valueMessage !== dupMsg);

        const shouldShowDup = isDupGroup && !hasBlockingError && k !== undefined;

        let valueMessage: string | undefined;
        if (shouldShowDup) valueMessage = dupMsg;
        else if (band.valueMessage === dupMsg) valueMessage = '';
        else valueMessage = band.valueMessage;

        const isValid = !band.fromMessage && !valueMessage;
        return { ...band, valueMessage, isValid };
    });
}

export function discreteBandsDifferFromMerge(
    current: Readonly<EditableDiscountBand>[],
    merged: Readonly<EditableDiscountBand>[]
): boolean {
    if (current.length !== merged.length) return true;
    for (let i = 0; i < current.length; i++) {
        const a = current[i];
        const b = merged[i];
        if (
            a.from !== b.from ||
            a.value !== b.value ||
            a.fromMessage !== b.fromMessage ||
            a.valueMessage !== b.valueMessage ||
            a.isValid !== b.isValid
        ) {
            return true;
        }
    }
    return false;
}